import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { arrayEquals } from '@wajeez-web/core/utils';
import React from 'react';
import dayjs from 'dayjs';

interface AutoSelectDatesPanelProps {
  oldestDate: Date;
  selectedDates?: Date[];
  setInputDates: (dates: Date[]) => void;
  setIsOpen?: (isOpen: boolean) => void;
  direction?: 'vertical' | 'horizontal';
}

const AutoSelectDatesPanel: React.FC<AutoSelectDatesPanelProps> = ({
  oldestDate,
  setInputDates,
  setIsOpen,
  selectedDates = [],
  direction = 'vertical',
}) => {
  const currentDate = new Date();
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    return `${month} ${day}, '${Number(year.toString().slice(-2))}`;
  };

  const formatDateWithoutYear = (date: Date) => {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });

    return `${month} ${day}`;
  };

  const getLastDayInLastMonth = () => {
    return new Date(
      new Date(Date.UTC(currentYear, currentMonth, 1)).getTime() -
        24 * 3600 * 1000
    ).getDate();
  };

  const getLastDateInLastMonth = () => {
    return new Date(
      Date.UTC(
        currentMonth === 0 ? currentYear - 1 : currentYear,
        currentMonth === 0 ? 11 : currentMonth - 1,
        getLastDayInLastMonth()
      )
    );
  };

  const getFirstDateInLastNthMonths = (months: number) => {
    return new Date(
      Date.UTC(
        currentMonth < months ? currentYear - 1 : currentYear,
        currentMonth < months
          ? 11 + (currentMonth - (months - 1))
          : currentMonth - months,
        1
      )
    );
  };

  const getAutoSelectDates = () => {
    return [
      {
        label: 'Last 30 days',
        dates: [new Date(Date.now() - 30 * 24 * 3600 * 1000), currentDate],
      },
      {
        label: 'This month',
        dates: [new Date(Date.UTC(currentYear, currentMonth, 1)), currentDate],
      },
      {
        label: 'Last month',
        dates: [getFirstDateInLastNthMonths(1), getLastDateInLastMonth()],
      },
      {
        label: 'Last 3 months',
        dates: [getFirstDateInLastNthMonths(3), getLastDateInLastMonth()],
      },
      {
        label: 'Last 6 months',
        dates: [getFirstDateInLastNthMonths(6), getLastDateInLastMonth()],
      },
      {
        label: 'This year',
        dates: [new Date(Date.UTC(currentYear, 0, 1)), currentDate],
      },
      {
        label: 'Last year',
        dates: [
          new Date(Date.UTC(currentYear - 1, 0, 1)),
          new Date(Date.UTC(currentYear - 1, 11, 31)),
        ],
      },
      {
        label: 'All time',
        dates: [oldestDate, currentDate],
      },
    ];
  };

  const getAutoSelectDatesTexts = () => {
    const intervals = getAutoSelectDates().map((interval) => interval.dates);

    return [
      `${formatDateWithoutYear(intervals[0][0])} - ${formatDateWithoutYear(
        intervals[0][1]
      )}`,
      `${formatDateWithoutYear(intervals[1][0])} - ${formatDateWithoutYear(
        intervals[1][1]
      )}`,
      `${formatDateWithoutYear(intervals[2][0])} - ${formatDateWithoutYear(
        intervals[2][1]
      )}`,
      `${formatDateWithoutYear(intervals[3][0])} - ${formatDateWithoutYear(
        intervals[3][1]
      )}`,
      `${formatDateWithoutYear(intervals[4][0])} - ${formatDateWithoutYear(
        intervals[4][1]
      )}`,
      `${formatDateWithoutYear(intervals[5][0])} - ${formatDateWithoutYear(
        intervals[5][1]
      )}`,
      `${formatDate(intervals[6][0])} - ${formatDate(intervals[6][1])}`,
      `${formatDate(intervals[7][0])} - ${formatDate(intervals[7][1])}`,
    ];
  };

  const renderAutoSelectDates = () => {
    return getAutoSelectDates().map((interval, index) => {
      const { label, dates } = interval;

      return (
        <li
          key={interval.label + '-' + index}
          className={`inline-block w-full pt-2 pb-[9px] space-s-2 ps-4 cursor-pointer ${
            index !== getAutoSelectDates().length - 1 && 'border-b-2'
          } border-[#f0f0f0] hover:bg-warmGray-100`}
          onClick={() => {
            setInputDates(dates);
            setIsOpen && setIsOpen(false);
          }}
        >
          <span className=" text-[14px]">
            <ReturnTranslatedText text={`date.${label}`} />
          </span>{' '}
          <span className=" text-textSecondary text-[12px]">
            {getAutoSelectDatesTexts()[index]}
          </span>
        </li>
      );
    });
  };

  const renderHorizontalAutoSelectDates = () => {
    return getAutoSelectDates().map((interval, index) => {
      const { label, dates } = interval;
      return (
        <li
          key={interval.label + '-' + index}
          className={`${
            arrayEquals(
              dates.map((item) => dayjs(item).format('DD/MM/YYYY')),
              selectedDates.map((item) => dayjs(item).format('DD/MM/YYYY'))
            )
              ? 'bg-primary text-white'
              : 'bg-gray-100'
          } rounded-full px-2 cursor-pointer flex justify-center items-center w-[100px] h-[40px] text whitespace-nowrap`}
          onClick={() => {
            setInputDates(dates);
            setIsOpen && setIsOpen(false);
          }}
        >
          <ReturnTranslatedText text={`date.${label}`} />
        </li>
      );
    });
  };

  return (
    <div
      dir="rtl"
      className="w-[280px] ltr:border-r-2 rtl:border-l-2 border-[#f0f0f0]"
    >
      {direction === 'vertical' && (
        <ul className="mb-0">{renderAutoSelectDates()}</ul>
      )}
      {direction === 'horizontal' && (
        <div className="flex space-s-2">
          {renderHorizontalAutoSelectDates()}
        </div>
      )}
    </div>
  );
};

export default AutoSelectDatesPanel;
